export interface TripPlannerMarkdownElement {
  type: 'text' | 'heading' | 'list' | 'listItem' | 'bold' | 'paragraph' | 'spacer' | 'line';
  content: string;
  level?: number;
  spacing?: number;
  children?: TripPlannerMarkdownElement[];
}

export const parseTripPlannerMarkdown = (text: string): TripPlannerMarkdownElement[] => {
  if (!text) return [];

  const lines = text.split('\n');
  const elements: TripPlannerMarkdownElement[] = [];
  let currentList: TripPlannerMarkdownElement | null = null;
  let emptyLineCount = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      emptyLineCount++;

      if (currentList) {
        elements.push(currentList);
        currentList = null;
      }
      continue;
    }

    if (emptyLineCount > 0) {
      elements.push({
        type: 'spacer',
        content: '',
        spacing: emptyLineCount,
      });
      emptyLineCount = 0;
    }

    if (trimmedLine.startsWith('####')) {
      if (currentList) {
        elements.push(currentList);
        currentList = null;
      }
      elements.push({
        type: 'heading',
        content: trimmedLine.replace(/^####\s+/, ''),
        level: 4,
      });
      continue;
    }

    if (trimmedLine.startsWith('###')) {
      if (currentList) {
        elements.push(currentList);
        currentList = null;
      }
      elements.push({
        type: 'heading',
        content: trimmedLine.replace(/^###\s+/, ''),
        level: 3,
      });
      continue;
    }

    if (trimmedLine.startsWith('- ')) {
      const itemContent = trimmedLine.replace(/^-\s+/, '');

      if (!currentList) {
        currentList = {
          type: 'list',
          content: '',
          children: [],
        };
      }

      currentList.children!.push({
        type: 'listItem',
        content: itemContent,
      });
      continue;
    }

    if (trimmedLine.includes(':') && !trimmedLine.endsWith('?') && !trimmedLine.endsWith(':')) {
      if (currentList) {
        elements.push(currentList);
        currentList = null;
      }

      elements.push({
        type: 'line',
        content: trimmedLine,
      });
      continue;
    }

    if (currentList) {
      elements.push(currentList);
      currentList = null;
    }

    elements.push({
      type: 'paragraph',
      content: trimmedLine,
    });
  }

  if (currentList) {
    elements.push(currentList);
  }

  return elements;
};

export const processTripPlannerBoldText = (text: string): (string | { type: 'bold'; content: string })[] => {
  if (!text) return [];

  const parts = text.split(/(\*\*[^*]+\*\*)/);
  return parts
    .filter(part => part.length > 0)
    .map(part => {
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return {
          type: 'bold' as const,
          content: part.slice(2, -2),
        };
      }
      return part;
    });
};
